import React, { useState } from 'react';
import {
  ChevronDown, ChevronRight, Trash2,
} from 'lucide-react';
import type { KnowledgeDocument } from '@/lib/api/knowledge';
import { getCategoryColor } from './KnowledgeTab';

interface Props {
  documents: KnowledgeDocument[];
  isSearchResult?: boolean;
  onDelete: (docId: string) => void;
  activeCategory?: string;
}

export const KnowledgeDocList: React.FC<Props> = ({
  documents,
  isSearchResult = false,
  onDelete,
  activeCategory,
}) => {
  const grouped: Record<string, KnowledgeDocument[]> = {};
  for (const doc of documents) {
    const cat = doc.metadata?.category || 'default';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(doc);
  }
  const categoryNames = Object.keys(grouped).sort();

  const showFlat = !!activeCategory || isSearchResult || categoryNames.length <= 1;

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleCollapse = (cat: string) =>
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));

  if (documents.length === 0) return null;

  if (showFlat) {
    return (
      <div className="space-y-1.5">
        {documents.map((doc) => (
          <DocItem key={doc.doc_id} doc={doc} onDelete={onDelete} showCategory={!activeCategory} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {categoryNames.map((cat) => {
        const docs = grouped[cat];
        const color = getCategoryColor(cat);
        const isCollapsed = collapsed[cat] ?? false;

        return (
          <div key={cat} className={`border rounded-lg ${color.border} overflow-hidden`}>
            <button
              type="button"
              onClick={() => toggleCollapse(cat)}
              className={`w-full flex items-center justify-between px-4 py-2.5 ${color.bg} hover:opacity-90 transition-opacity`}
            >
              <div className="flex items-center gap-2">
                {isCollapsed
                  ? <ChevronRight className={`w-4 h-4 ${color.text}`} />
                  : <ChevronDown className={`w-4 h-4 ${color.text}`} />}
                <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                <span className={`text-sm font-medium ${color.text}`}>{cat}</span>
              </div>
              <span className={`text-xs ${color.text} opacity-70`}>{docs.length} 条</span>
            </button>

            {!isCollapsed && (
              <div className="divide-y divide-slate-100">
                {docs.map((doc) => (
                  <DocItem key={doc.doc_id} doc={doc} onDelete={onDelete} showCategory={false} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DocItem: React.FC<{
  doc: KnowledgeDocument;
  onDelete: (id: string) => void;
  showCategory: boolean;
}> = ({ doc, onDelete, showCategory }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = doc.content.length > 120;
  const displayText = expanded || !isLong ? doc.content : doc.content.slice(0, 120) + '...';
  const cat = doc.metadata?.category;
  const color = cat ? getCategoryColor(cat) : null;

  return (
    <div className="px-4 py-2.5 hover:bg-slate-50/50 transition-colors group flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm text-slate-700 leading-relaxed ${isLong ? 'cursor-pointer' : ''}`}
          onClick={() => isLong && setExpanded(!expanded)}
        >
          {displayText}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {showCategory && cat && color && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded ${color.bg} ${color.text}`}>
              {cat}
            </span>
          )}
          {doc.relevance_score != null && (
            <span className="text-xs text-blue-500">
              {(doc.relevance_score * 100).toFixed(0)}% 相关
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => onDelete(doc.doc_id)}
        className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100 shrink-0"
        title="删除"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
